import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import type { LocalizedString } from '../paginated-table.component';

export interface FilterOperatorDefinitionBase {
  label: LocalizedString
  operator: string
  field?: string
  extra_param?: string
}

export interface TextFilterOperatorDefinition extends FilterOperatorDefinitionBase {
  inputType: 'text'
}

export interface MultiselectFilterOperatorDefinition extends FilterOperatorDefinitionBase {
  inputType: 'multiselect'
  possibleValues: { label: string, value: string }[]
}

export type FilterOperatorDefinition = TextFilterOperatorDefinition | MultiselectFilterOperatorDefinition;

export interface FilterDefinition {
  label: LocalizedString
  operators: FilterOperatorDefinition[]
}

export interface Filter {
  filterDef: FilterDefinition
  operator: FilterOperatorDefinition
  query: string | { label: string, value: string }[] | null
  suggestions?: { label: string, value: string }[]
}

@Component({
  selector: 'app-filter-panel',
  templateUrl: './filter-panel.component.html',
  styleUrl: './filter-panel.component.scss',
  standalone: false
})
export class FilterPanelComponent implements OnInit {
  @Input() filterDefs!: FilterDefinition[];
  @Input() isLoading = false;

  protected filters: Filter[] = [];

  @Output() filterQueryParameterChange = new EventEmitter<any>();

  ngOnInit(): void {
    this.addFilter()
  }

  addFilter() {
    this.filters.push({ filterDef: this.filterDefs[0], operator: this.filterDefs[0].operators[0], query: null })
  }

  removeFilter(filter: Filter) {
    const index = this.filters.indexOf(filter);
    if (index > -1) {
      this.filters.splice(index, 1);
    }
  }

  createFilterQueryParameter() {
    let notEmptyFilters = this.filters.filter(f => {
      if (['text', 'multiselect'].includes(f.operator.inputType)) {
        const query = f.query
        if (Array.isArray(query)) {
          return query.length > 0;
        }
        if (typeof query === 'string') {
          return query.trim() !== '';
        }
        return query;
      }
      return true
    });

    if (notEmptyFilters.length === 0) {
      return null;
    }
    
    return {
      group: {
        operator: 'AND',
        filters: notEmptyFilters.map(f => {
          const query = (typeof f.query === 'string') ?  f.query : f.query?.map(q => q.value);
          return {
            filter: {
              [f.operator.operator]: {
                field: f.operator.field,
                extra_param: f.operator.extra_param,
                query: query
              }
            }
          }
        })
      }
    }
  }

  onFilterChange() {
    if (this.isLoading) {
      return;
    }
    const filterQueryParameter = this.createFilterQueryParameter();
    this.filterQueryParameterChange.emit(filterQueryParameter);
  }

  multiselectComplete(event: AutoCompleteCompleteEvent, filter: Filter) {
    if (filter.operator.inputType == 'multiselect') {
      filter.suggestions = filter.operator.possibleValues.filter(
        possibleValue => possibleValue.label.toLowerCase().includes(event.query.toLowerCase())
      );
    }
  }

  setFilterOperator(filter: Filter, operator: FilterOperatorDefinition) {
    filter.operator = operator;
    filter.query = null; // Reset query when changing operator
    filter.suggestions = undefined // Reset suggestions when changing operator
  }

  // This function is needed to prevent the PrimeNG `AutoComplete` component
  // from auto selecting the `item.value` field.
  queryAutocompleteValueFn(item: any): any {
    return item;
  }
}
